import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Box,
  useToast,
  Icon,
} from "@chakra-ui/react";
import { FiX } from "react-icons/fi";
import CSSAvatar from "./CSSAvatar";
import { patientMessagingApi } from "../features/messaging/api";

export default function GroupManagementModal({
  isOpen,
  onClose,
  group,
  currentUserId,
  patient,
  dashState,
  onLeaveGroup,
}) {
  const toast = useToast();
  const [isLeaving, setIsLeaving] = useState(false);
  
  if (!group) return null;
  
  const members = group.members || [];

  // Check if current user is admin (isAdmin is true for owner and admin)
  const currentMember = members.find((m) => m.kind === "patient" && m.id === currentUserId);
  const isCurrentUserAdmin = Boolean(currentMember?.isAdmin);

  // Sort members: owner first, then admins, then regular members
  const sortedMembers = [...members].sort((memberA, memberB) => {
    if (memberA.role === 'owner') return -1;
    if (memberB.role === 'owner') return 1;
    if (memberA.isAdmin && !memberB.isAdmin) return -1;
    if (!memberA.isAdmin && memberB.isAdmin) return 1;
    return 0;
  });

  // Leave group handler
  const handleLeaveGroup = async () => {
    setIsLeaving(true);
    
    try {
      await patientMessagingApi.leaveConversation({ jwt: dashState?.jwt }, group.conversationId);

      toast({
        title: "Left group successfully",
        status: "success",
        duration: 3000,
      });
      
      // Call parent handler to update UI
      if (onLeaveGroup) {
        onLeaveGroup(group.conversationId);
      }
      
      onClose();
    } catch (error) {
      console.error("Error leaving group:", error);
      toast({
        title: "Failed to leave group",
        description: error?.message || "Please try again",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLeaving(false);
    }
  };
  
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="md"
      isCentered
    >
      <ModalOverlay bg="rgba(0, 0, 0, 0.4)" />
      <ModalContent>
        <ModalHeader 
          fontSize="18px" 
          fontWeight="600" 
          color="gray.900"
          borderBottom="1px solid"
          borderColor="gray.200"
          pb={3}
        >
          <HStack justify="space-between" align="center">
            <Text>Manage Group: {group.title || "Care Group"}</Text>
            <ModalCloseButton 
              position="relative"
              right={0}
              top={0}
              size="sm"
            />
          </HStack>
        </ModalHeader>
        
        <ModalBody py={5}>
          {/* Current Members Section */}
          <Box>
            <Text fontSize="14px" color="gray.700" fontWeight="500" mb={4}>
              Current Members ({group.members?.length ?? 0})
            </Text>
            
            <VStack spacing={0} align="stretch">
              {sortedMembers.map((member) => {
                const isCurrentUser = member.kind === "patient" && member.id === currentUserId;
                const isPatient = member.kind === "patient" && member.id === patient?.pid;
                const isOwner = member.role === 'owner';
                const isAdmin = member.isAdmin;
                
                return (
                  <HStack 
                    key={`${member.kind}:${member.id}`} 
                    py={3}
                    px={4}
                    bg="white"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                    transition="background 0.15s ease"
                  >
                    <CSSAvatar user={member} size="sm" />
                    <VStack align="start" spacing={0} flex={1} ml={3}>
                      <HStack spacing={2}>
                        <Text fontSize="14px" fontWeight="500" color="gray.900">
                          {member.name} {isCurrentUser && "(You)"}
                        </Text>
                        {isOwner && (
                          <Badge 
                            bg="gray.800" 
                            color="white" 
                            fontSize="10px" 
                            px={2} 
                            py={0.5}
                            borderRadius="4px"
                            fontWeight="600"
                            textTransform="uppercase"
                          >
                            OWNER
                          </Badge>
                        )}
                        {!isOwner && isAdmin && (
                          <Badge 
                            bg="gray.600" 
                            color="white" 
                            fontSize="10px" 
                            px={2} 
                            py={0.5}
                            borderRadius="4px"
                            fontWeight="600"
                            textTransform="uppercase"
                          >
                            ADMIN
                          </Badge>
                        )}
                        {isPatient && (
                          <Badge 
                            bg="green.500" 
                            color="white" 
                            fontSize="10px" 
                            px={2} 
                            py={0.5}
                            borderRadius="4px"
                            fontWeight="600"
                            textTransform="uppercase"
                          >
                            PATIENT
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="13px" color="gray.500" textTransform="capitalize">
                        {member.kind}
                      </Text>
                    </VStack>
                  </HStack>
                );
              })}
            </VStack>
            
            {!isCurrentUserAdmin && (
              <Text fontSize="13px" color="gray.500" textAlign="center" mt={5}>
                Only group admins can add new members
              </Text>
            )}
          </Box>
        </ModalBody>
        
        <ModalFooter borderTop="1px solid" borderColor="gray.200" pt={4} justifyContent="space-between">
          <Button
            color="red.500"
            variant="ghost"
            onClick={handleLeaveGroup}
            fontSize="14px"
            fontWeight="500"
            _hover={{ bg: "red.50" }}
            isLoading={isLeaving}
            loadingText="Leaving..."
          >
            Leave Group
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            fontSize="14px"
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}